import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { apiService } from 'utils/api';
import { Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import { BrandType } from 'models/brand.model';
import ManageTrafficCard from './manage-traffic-card';
import useDashboardContext from 'contexts/dashboard.context';
import ManageTrafficModal from 'components/kitchen/modals/manage-traffic-modal';
import ConfirmTrafficModal from 'components/kitchen/modals/manage-traffic-modal/confirm-traffic-modal';
import TrafficSuccessModal from 'components/kitchen/modals/manage-traffic-modal/success-modal';
import { STORAGE_KEYS } from 'utils/constants';
import { CoKitchenType } from 'models/cokitchen.model';
import moment from 'moment';

const ManageTraffic = () => {
  const { activeCokitchen, setActiveCokitchen, checkReactivationTime } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [showKitchenOffModal, setShowKitchenOffModal] = useState(false);
  const [showKitchenOnModal, setShowKitchenOnModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [kitchenOnLoading, setKitchenOnLoading] = useState(false);

  const kitchenIsBusy = useMemo(
    () =>
      activeCokitchen.reactivation_time
        ? moment().isBefore(activeCokitchen.reactivation_time)
        : activeCokitchen.is_busy,
    [activeCokitchen]
  );

  const toggleKitchen = () => {
    if (kitchenIsBusy) setShowKitchenOnModal(true);
    else setShowKitchenOffModal(true);
  };

  const getBrands = async (refetch?: boolean) => {
    if (refetch) setLoading(true);
    const data = {
      archived: false,
      cokitchen_id: activeCokitchen.id
    };
    try {
      const res = await apiService.get('v2/brand_management/brands', {
        params: data,
        baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
      });
      setBrands(
        res?.data?.data?.map((data: BrandType) => ({
          ...data,
          is_busy: data.reactivation_time ? moment().isBefore(data.reactivation_time) : data.is_busy
        }))
      );
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const turnOnKitchen = async () => {
    if (!activeCokitchen?.id) return;
    setKitchenOnLoading(true);
    const data = { is_busy: false, reactivation_time: null };
    try {
      await apiService.put(`traffic-control/cokitchen/${activeCokitchen.id}`, {
        data,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      setShowKitchenOnModal(false);
      setShowSuccessModal(true);
      updateCokitchen(false, null);
    } finally {
      setKitchenOnLoading(false);
    }
  };

  const updateCokitchen = (is_busy: boolean, reactivation_time: string | null) => {
    const cokitchenList: CoKitchenType[] = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
    );
    const cokitchenIndex = cokitchenList.findIndex((c) => c.id === activeCokitchen.id);
    if (cokitchenIndex > -1) {
      const cokitchen = { ...activeCokitchen, is_busy, reactivation_time };
      cokitchenList[cokitchenIndex] = cokitchen;
      localStorage.setItem(STORAGE_KEYS.COKITCHEN_LIST, JSON.stringify(cokitchenList));
      setActiveCokitchen(cokitchen);
    }
  };

  useEffect(() => {
    if (activeCokitchen.id) {
      getBrands();
    }
  }, [activeCokitchen.id, checkReactivationTime]);

  return (
    <div className='w-full mr-11'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-[22px] font-bold'>Manage traffic</h1>
        <div className='border rounded-md p-1 border-[#E5E6E6] flex items-center'>
          <p className='mr-2 text-[#5E6164] px-1'>Turn {kitchenIsBusy ? 'on' : 'off'} location</p>
          <Switch active={!kitchenIsBusy} onChange={toggleKitchen} />
        </div>
      </div>

      {loading ? (
        <div className='w-full min-h-[60vh] flex items-center justify-center'>
          <p className='text-center'>Loading...</p>
        </div>
      ) : (
        <div className='grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-4'>
          {brands.map((item) => (
            <ManageTrafficCard
              brand={item}
              key={item.id + item.is_busy}
              isKitchenBusy={kitchenIsBusy}
            />
          ))}
        </div>
      )}

      {showKitchenOffModal && (
        <ManageTrafficModal
          open
          type='cokitchen'
          cokitchen={activeCokitchen}
          afterMutation={updateCokitchen}
          onClose={() => setShowKitchenOffModal(false)}
        />
      )}

      {showKitchenOnModal && (
        <ConfirmTrafficModal
          open
          hideEmoji
          title={`Are you sure you want to activate ${activeCokitchen.name}?`}
          description='Once confirmed, orders for this location will be accepted.'
          loading={kitchenOnLoading}
          onConfirm={turnOnKitchen}
          onClose={() => {
            setShowKitchenOnModal(false);
            setShowSuccessModal(false);
          }}
        />
      )}

      {showSuccessModal && (
        <TrafficSuccessModal
          open
          title={`${activeCokitchen.name} has been turned on`}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
    </div>
  );
};

export default ManageTraffic;
